import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type MutableRefObject,
} from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { getIsArchivedForBoard } from "@/other/utils/otherUtils.jsx";
import type { User } from "@/bryntum-lib/models/User";
import type { ISwivuProject } from "@/types/SwivuProject";
import type { Period, TabId } from "../types";

export type UserSort = "name" | "age" | "points" | "tasks";

export type UserListStats = {
  resourceCount: number;
  pointsTotal: number;
  scheduledHours: number;
  taskCount: number;
};

function emptyStats(): UserListStats {
  return { resourceCount: 0, pointsTotal: 0, scheduledHours: 0, taskCount: 0 };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BoardUser = User & Record<string, any>;

export function userIsDeactivated(user: { isDeactivated?: boolean } | null | undefined): boolean {
  try { return Boolean(user?.isDeactivated); } catch { return false; }
}

export function userRoleName(user: { roleName?: string } | null | undefined, fallback = ""): string {
  try { return user?.roleName ?? fallback; } catch { return fallback; }
}

function safeRoleName(user: BoardUser): string {
  return userRoleName(user, "Other");
}

export function compareUsers(
  a: BoardUser,
  b: BoardUser,
  sortBy: UserSort,
  statsMap?: Map<string | number, UserListStats>,
): number {
  switch (sortBy) {
    case "age": {
      const aDate = a.get?.("createdAt") ? new Date(a.get("createdAt")).getTime() : 0;
      const bDate = b.get?.("createdAt") ? new Date(b.get("createdAt")).getTime() : 0;
      return aDate - bDate;
    }
    case "points": {
      const ap = statsMap?.get(a.id)?.pointsTotal ?? 0;
      const bp = statsMap?.get(b.id)?.pointsTotal ?? 0;
      return bp - ap;
    }
    case "tasks": {
      const at = statsMap?.get(a.id)?.taskCount ?? 0;
      const bt = statsMap?.get(b.id)?.taskCount ?? 0;
      return bt - at;
    }
    case "name":
    default:
      return (a.name ?? "").localeCompare(b.name ?? "");
  }
}

interface UsersContextValue {
  users: BoardUser[];
  filteredUsers: BoardUser[];
  availableRoles: string[];
  roleFilters: string[];
  toggleRoleFilter: (role: string) => void;
  clearRoleFilters: () => void;
  sortBy: UserSort;
  setSortBy: (sort: UserSort) => void;
  selectedUserId: number | string;
  selectedUser: BoardUser | undefined;
  selectUser: (id: number | string) => void;
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
  query: string;
  setQuery: (q: string) => void;
  period: Period;
  setPeriod: (p: Period) => void;
  project: ISwivuProject | null;
  showArchived: boolean;
  /** Pre-computed resource count, points, and scheduled hours per user — consumed by the sidebar. */
  userStatsMap: Map<string | number, UserListStats>;
  /** Ref attached to the tab scroll container — used by virtualizers in tab content. */
  tabScrollRef: MutableRefObject<HTMLElement | null>;
}

const UsersContext = createContext<UsersContextValue | undefined>(undefined);

export const ROLE_ORDER = [
  "Administrator",
  "Executive",
  "Office",
  "Scheduler",
  "Workforce",
  "Field Supervisor",
  "Crew Lead",
  "Field Tech",
  "Deactivated",
];

export function sortRoles(roles: string[]) {
  return [...roles].sort((a, b) => {
    const ia = ROLE_ORDER.indexOf(a);
    const ib = ROLE_ORDER.indexOf(b);
    const ka = ia === -1 ? 1000 : ia;
    const kb = ib === -1 ? 1000 : ib;
    if (ka !== kb) return ka - kb;
    return a.localeCompare(b);
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function groupKeyFor(user: any): string {
  if (userIsDeactivated(user)) return "Deactivated";
  return userRoleName(user) || "Other";
}
export function UsersProvider({ children }: { children: ReactNode }) {
  const { project } = useSwivuBryntumContext();
  const { version } = useSwivuStore({ storeId: STORE_KEYS.boardUserStore });
  const { version: resourceVersion } = useSwivuStore({ storeId: STORE_KEYS.resourceStore });
  const { version: pointsVersion } = useSwivuStore({ storeId: STORE_KEYS.incentivePointsStore });
  const { version: eventVersion } = useSwivuStore({ storeId: STORE_KEYS.eventStore });

  const tabScrollRef = useRef<HTMLElement | null>(null);

  const showArchived: boolean = useMemo(() => {
    const slug = (project?.boardStore?.first as any)?.slug;
    return Boolean(getIsArchivedForBoard(slug));
  }, [project]);

  const users = useMemo<BoardUser[]>(
    () => project?.boardUsersStore?.query(() => true) as BoardUser[] ?? [],
    [version, project],
  );

  /** Pre-compute resource count, points, scheduled hours, and task count for sidebar + sort. */
  const statsMap = useMemo(() => {
    const map = new Map<string | number, UserListStats>();
    if (!project) return map;
    const resources = project.resourceStore.query(() => true);
    const points = project.incentivePointsStore.query(() => true);
    const resourceToUsers = new Map<string | number, (string | number)[]>();

    for (const r of resources) {
      const ids: (number | string)[] = (r as any).assignedBoardUsers ?? [];
      if (ids.length > 0) resourceToUsers.set(r.id, ids);
      for (const uid of ids) {
        const cur = map.get(uid) ?? emptyStats();
        cur.resourceCount += 1;
        map.set(uid, cur);
      }
    }
    for (const p of points) {
      if (p.archived) continue;
      const uid = p.boardUserId;
      const cur = map.get(uid) ?? emptyStats();
      cur.pointsTotal += p.points ?? 0;
      map.set(uid, cur);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const events = (project as any).eventStore?.records ?? [];
    for (const evt of events) {
      const userIds = resourceToUsers.get(evt.resourceId);
      if (!userIds?.length) continue;
      if (!evt.startDate || !evt.endDate) continue;
      const hours =
        (new Date(evt.endDate).getTime() - new Date(evt.startDate).getTime()) / 3_600_000;
      if (!Number.isFinite(hours) || hours <= 0) continue;
      for (const uid of userIds) {
        const cur = map.get(uid) ?? emptyStats();
        cur.scheduledHours += hours;
        cur.taskCount += 1;
        map.set(uid, cur);
      }
    }

    for (const [uid, cur] of map) {
      cur.scheduledHours = Math.round(cur.scheduledHours);
      map.set(uid, cur);
    }

    return map;
  }, [project, resourceVersion, pointsVersion, eventVersion]);

  const [selectedUserId, setSelectedUserId] = useState<number | string>(users[0]?.id ?? 0);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [query, setQuery] = useState("");
  const [period, setPeriod] = useState<Period>("14D");
  const [roleFilters, setRoleFilters] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<UserSort>("name");

  const availableRoles = useMemo(() => {
    const keys = new Set(users.map(groupKeyFor));
    return sortRoles([...keys]);
  }, [users]);

  const filteredUsers = useMemo(() => {
    let list = users;
    if (roleFilters.length > 0) {
      const allowed = new Set(roleFilters);
      list = list.filter((u) => allowed.has(groupKeyFor(u)));
    }
    const s = query.trim().toLowerCase();
    if (s) {
      list = list.filter((u) => {
        const rn = safeRoleName(u).toLowerCase();
        return (
          (u.name ?? "").toLowerCase().includes(s) ||
          rn.includes(s) ||
          (u.email ?? "").toLowerCase().includes(s)
        );
      });
    }
    return [...list].sort((a, b) => compareUsers(a, b, sortBy, statsMap));
  }, [users, query, roleFilters, sortBy, statsMap]);

  useEffect(() => {
    if (filteredUsers.length === 0) return;
    if (filteredUsers.some((u) => u.id === selectedUserId)) return;
    setSelectedUserId(filteredUsers[0].id);
  }, [filteredUsers, selectedUserId]);

  useEffect(() => {
    if (users.length > 0 && (!selectedUserId || selectedUserId === 0)) {
      setSelectedUserId(users[0].id);
    }
  }, [users, selectedUserId]);

  const selectedUser = useMemo(
    () =>
      filteredUsers.find((u) => u.id === selectedUserId) ??
      users.find((u) => u.id === selectedUserId) ??
      users[0],
    [filteredUsers, users, selectedUserId],
  );

  const selectUser = useCallback((id: number | string) => {
    setSelectedUserId(id);
    setActiveTab("overview");
  }, []);

  const toggleRoleFilter = useCallback((role: string) => {
    setRoleFilters((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  }, []);

  const clearRoleFilters = useCallback(() => setRoleFilters([]), []);

  const value = useMemo(
    () => ({
      users,
      filteredUsers,
      availableRoles,
      roleFilters,
      toggleRoleFilter,
      clearRoleFilters,
      sortBy,
      setSortBy,
      selectedUserId,
      selectedUser,
      selectUser,
      activeTab,
      setActiveTab,
      query,
      setQuery,
      period,
      setPeriod,
      project: project ?? null,
      showArchived,
      userStatsMap: statsMap,
      tabScrollRef,
    }),
    [
      users,
      filteredUsers,
      availableRoles,
      roleFilters,
      toggleRoleFilter,
      clearRoleFilters,
      sortBy,
      selectedUserId,
      selectedUser,
      selectUser,
      activeTab,
      query,
      period,
      project,
      showArchived,
      statsMap,
      tabScrollRef,
    ],
  );

  return <UsersContext.Provider value={value}>{children}</UsersContext.Provider>;
}

export function useUsersContext() {
  const ctx = useContext(UsersContext);
  if (!ctx) {
    throw new Error("useUsersContext must be used within UsersProvider");
  }
  return ctx;
}
